import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CreateAgent from './components/CreateAgent';
import MessageWindow from './components/MessageWindow';
import PrivateRoute, { RequireRole } from './components/PrivateRoute';
import AIAssistantManagement from './components/AIAssistantManagement';
import AIAssistant from './components/AIAssistant';
import UserParameter from './components/UserParameter';
import Archive from './components/Archive';
import ResetPassword from './components/ResetPassword';
import ForgotPassword from './components/ForgotPassword';
import PrivacyPolicy from './components/PrivacyPolicy.js';
import InstagramAccountSelection from './components/InstagramAccountSelection';
import KnowledgeBoard from './components/KnowledgeBoard';
import Welcome from './components/Welcome';
import Statistics from './components/Statistics';
import Layout from './components/Layout';
import Billing from './components/ShopifyBillingTest';
import ShopifyCallback from './components/ShopifyCallback';
import ChatbotSettingsPage from './components/ChatbotSettingsPage.js'; // La nouvelle page
import Brands from './components/brands';
import NotificationsSettings from './components/NotificationsSettings.js';
import TrackingSettings from './components/TrackingSettings.js';
import FollowUpSettings from './components/FollowUpSettings.js';
import ParcelMonitor from './components/ParcelMonitor.js';
import StatusInstructions from './components/StatusInstructions.js';
import Carriers from './components/Carriers.js';
import CarrierDetail from './components/CarrierDetail.js';
import DataSources from './components/DataSources.js';
import Onboarding from './components/onboarding/Onboarding';
import TicketViewsParameters from './components/TicketViewsParameters.js';
import TicketFormSettings from './components/TicketFormSettings.js';

//import AppInitializer from './components/AppInitializer';
import LanguageSwitcher from './components/LanguageSwitcher';
import DownloadFile from "./components/DownloadFile.js"
import ShopifyRegister from './components/ShopifyRegister.js';
import LogisticsSignup from './components/LogisticsSignup.js';
import ColissimoParams from './components/ColissimoParams.js';
import NotFound from './components/NotFound';
import Clients from './components/Clients';
import ClientDetails from './components/ClientDetails';
import ClientsInstructions from './components/ClientsInstructions';
import ClientEDNDetail from './components/ClientEDNDetail';
import WarehousesInstructions from './components/WarehousesInstructions';
import WarehouseDetail from './components/WarehouseDetail';
import OpenAPISchemeManagement from './components/OpenAPISchemeManagement';
import AIAutomation from './components/AIAutomation';
import RouterAgent from './components/RouterAgent';
import Refunds from './components/Refunds';
import CommerceOrders from './components/commerce/CommerceOrders';
import CustomerSms from './components/customerSms/CustomerSms';
import ShopConnections from './components/shops/ShopConnections';
import RefundDetail from './components/RefundDetail';
import VoiceTickets from './components/VoiceTickets';
import VoiceAgents from './components/voiceSetup/VoiceAgents';
import VoiceAgentPage from './components/voiceSetup/VoiceAgentPage';
import VoiceNumbers from './components/voiceSetup/VoiceNumbers';
import VoiceTicketDetail from './components/VoiceTicketDetail';

// Catalogue produits (allow-listed tenants): its own chunk, loaded on first visit.
const CatalogueProducts = lazy(() => import('./components/catalogue/CatalogueProducts.js'));
const CatalogueProduct = lazy(() => import('./components/catalogue/CatalogueProduct.js'));
const CatalogueConnections = lazy(() => import('./components/catalogue/CatalogueConnections.js'));
const CatalogueSettings = lazy(() => import('./components/catalogue/CatalogueSettings.js'));
const lazyPage = (Page) => <Suspense fallback={null}><Page /></Suspense>;


const LoginPage = () => (
  <div className="login-page-container">
    <LanguageSwitcher />
    <Login />
  </div>
);

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/shopify/callback" element={<ShopifyCallback />} />
        <Route path="/download/:filename" element={<DownloadFile />} />
        <Route path="/shopify-register" element={<ShopifyRegister />} />
        <Route path="/signup" element={<LogisticsSignup />} />


        <Route
          path="/*"
          element={
            <PrivateRoute
              component={({ isAuthenticated, userRole }) => (
                <Layout userRole={userRole} />
              )}
            />
          }
        >
          <Route path="dashboard" element={<RequireRole roles={['admin', 'employee']}><Dashboard /></RequireRole>} />
          <Route path="voice-tickets" element={<RequireRole roles={['admin', 'logistics']}><VoiceTickets /></RequireRole>} />
          {/* Agents vocaux: their own section, separate from the text agents (2026-10-07). */}
          <Route path="voice-agents" element={<RequireRole roles={['admin']}><VoiceAgents /></RequireRole>} />
          <Route path="voice-agents/:agentId" element={<RequireRole roles={['admin']}><VoiceAgentPage /></RequireRole>} />
          <Route path="voice-numbers" element={<RequireRole roles={['admin']}><VoiceNumbers /></RequireRole>} />
          <Route path="voice-tickets/:callId" element={<RequireRole roles={['admin', 'logistics']}><VoiceTicketDetail /></RequireRole>} />
          <Route path="parameters" element={<RequireRole roles={['admin', 'logistics', 'employee']}><UserParameter /></RequireRole>} />
          <Route path="create-agent" element={<RequireRole roles={['admin', 'employee']}><CreateAgent /></RequireRole>} />
          <Route path="tickets/:ticketId/messages" element={<RequireRole roles={['admin', 'employee','logistics']}><MessageWindow /></RequireRole>} />
          <Route path="ai-assistants" element={<RequireRole roles={['admin']}><AIAssistantManagement /></RequireRole>} />
          <Route path="my-ai-assistants" element={<RequireRole roles={['admin']}><AIAssistant /></RequireRole>} />
          <Route path="statistics" element={<RequireRole roles={['admin']}><Statistics /></RequireRole>} />
          {/* Productivity now lives in a tab of the Employees page */}
          <Route path="employee-productivity" element={<Navigate to="/create-agent" replace />} />
          <Route path="refunds" element={<RequireRole roles={['admin', 'employee', 'logistics', 'logistician']}><Refunds /></RequireRole>} />
          <Route path="refunds/:refundId" element={<RequireRole roles={['admin', 'employee', 'logistics', 'logistician']}><RefundDetail /></RequireRole>} />
          <Route path="commerce/orders" element={<RequireRole roles={['admin', 'employee', 'logistics', 'logistician']}><CommerceOrders /></RequireRole>} />
          <Route path="shops" element={<RequireRole roles={['admin', 'logistics', 'logistician']}><ShopConnections /></RequireRole>} />
          <Route path="billing" element={<RequireRole roles={['admin']}><Billing /></RequireRole>} />
          <Route path="billing-success" element={<RequireRole roles={['admin']}><Billing /></RequireRole>} />
          <Route path="billing-failed" element={<RequireRole roles={['admin']}><Billing /></RequireRole>} />
          <Route path="knowledge-board" element={<RequireRole roles={['admin']}><KnowledgeBoard /></RequireRole>} />
          <Route path="tickets/archive" element={<RequireRole roles={['admin','logistics']}><Archive /></RequireRole>} />
          <Route path="chatbot-settings" element={<RequireRole roles={['admin', 'logistics']}><ChatbotSettingsPage /></RequireRole>} />
          <Route path="ticket-form-settings" element={<RequireRole roles={['admin', 'logistics']}><TicketFormSettings /></RequireRole>} />
          <Route path="instagram/account-selection" element={<RequireRole roles={['admin']}><InstagramAccountSelection /></RequireRole>} />
          <Route path="brands" element={<RequireRole roles={['admin']}><Brands /></RequireRole>} />
          <Route path="notifications-settings" element={<RequireRole roles={['admin']}><NotificationsSettings /></RequireRole>} />
          <Route path="tracking-settings" element={<RequireRole roles={['admin']}><TrackingSettings /></RequireRole>} />
          <Route path="followup-settings" element={<RequireRole roles={['admin', 'logistics']}><FollowUpSettings /></RequireRole>} />
          <Route path="parcel-monitor" element={<RequireRole roles={['admin', 'logistics']}><ParcelMonitor /></RequireRole>} />
          <Route path="sms-notifications" element={<RequireRole roles={['admin', 'logistics']}><CustomerSms /></RequireRole>} />
          <Route path="status-instructions" element={<RequireRole roles={['admin', 'logistics']}><StatusInstructions /></RequireRole>} />
          <Route path="carriers-instructions" element={<RequireRole roles={['admin']}><Carriers /></RequireRole>} />
          <Route path="carriers-instructions/:carrierId" element={<RequireRole roles={['admin', 'logistics']}><CarrierDetail /></RequireRole>} />
          <Route path="connectors" element={<RequireRole roles={['admin', 'logistics']}><DataSources /></RequireRole>} />
          <Route path="onboarding" element={<RequireRole roles={['admin']}><Onboarding /></RequireRole>} />
          <Route path="catalogue" element={<RequireRole roles={['admin', 'employee']}>{lazyPage(CatalogueProducts)}</RequireRole>} />
          <Route path="catalogue/products/:productId" element={<RequireRole roles={['admin', 'employee']}>{lazyPage(CatalogueProduct)}</RequireRole>} />
          <Route path="catalogue/connections" element={<RequireRole roles={['admin']}>{lazyPage(CatalogueConnections)}</RequireRole>} />
          <Route path="catalogue/settings" element={<RequireRole roles={['admin']}>{lazyPage(CatalogueSettings)}</RequireRole>} />
          <Route path="catalogue/assistant" element={<Navigate to="/catalogue?assistant=1" replace />} />
          <Route path="warehouses-instructions" element={<RequireRole roles={['admin']}><WarehousesInstructions /></RequireRole>} />
          <Route path="warehouses-instructions/:warehouseId" element={<RequireRole roles={['admin']}><WarehouseDetail /></RequireRole>} />
          <Route path="openapi-schemes" element={<RequireRole roles={['admin']}><OpenAPISchemeManagement /></RequireRole>} />
          <Route path="ai-automation" element={<RequireRole roles={['admin']}><AIAutomation /></RequireRole>} />
          <Route path="router-agent" element={<RequireRole roles={['admin']}><RouterAgent /></RequireRole>} />
          <Route path="ticket-views-parameters" element={<RequireRole roles={['admin']}><TicketViewsParameters /></RequireRole>} />
          <Route path="colissimo-params" element={<RequireRole roles={['admin']}><ColissimoParams /></RequireRole>} />
          <Route path="clients" element={<RequireRole roles={['admin', 'employee']}><Clients /></RequireRole>} />
          <Route path="clients/:clientId" element={<RequireRole roles={['admin', 'employee']}><ClientDetails /></RequireRole>} />
          <Route path="clients-instructions" element={<RequireRole roles={['admin']}><ClientsInstructions /></RequireRole>} />
          <Route path="clients-instructions/:clientId" element={<RequireRole roles={['admin', 'logistics']}><ClientEDNDetail /></RequireRole>} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Router>
  );
};

export default App;